import { useEffect, useRef } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { ArrowUpRight, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import logoWhite from "@/assets/logo-white.webp";
import LogoBars from "@/components/brand/LogoBars";

type SimplifiedMenuOverlayProps = {
  onClose: () => void;
  onNavIntent: () => void;
  onProjectReview: () => void;
  onRouteSelect: (href: string) => void;
  navLogoSequenceActive: boolean;
  navLogoProgress: number;
  navLogoTriggerId: number;
};

const primaryRoutes = [
  {
    index: "01",
    label: "Our Work",
    href: "/work",
    note: "Built work, in progress and complete.",
  },
  {
    index: "02",
    label: "The System",
    href: "/system",
    note: "Design, products, controls, and execution.",
  },
  {
    index: "03",
    label: "codelumenLabs",
    href: "/labs",
    note: "Private sessions for project teams.",
  },
  {
    index: "04",
    label: "Contact",
    href: "/contact",
    note: "Start a project, review, or Labs session.",
  },
] as const;

const secondaryRoutes = [
  { label: "Capabilities", href: "/solutions" },
  { label: "About", href: "/about" },
  { label: "Talent", href: "/talent" },
] as const;

const ease = [0.22, 1, 0.36, 1] as const;

const SimplifiedMenuOverlay = ({
  onClose,
  onNavIntent,
  onProjectReview,
  onRouteSelect,
  navLogoSequenceActive,
  navLogoProgress,
  navLogoTriggerId,
}: SimplifiedMenuOverlayProps) => {
  const location = useLocation();
  const reduceMotion = useReducedMotion();
  const menuRef = useRef<HTMLElement | null>(null);
  const closeButtonRef = useRef<HTMLButtonElement | null>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocusRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const focusFrame = window.requestAnimationFrame(() => {
      menuRef.current?.focus({ preventScroll: true });
    });

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab" || !menuRef.current) return;

      const focusable = Array.from(
        menuRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((element) => !element.hasAttribute("hidden"));

      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      } else if (!menuRef.current.contains(document.activeElement)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", handleKeyDown);
      previousFocusRef.current?.focus();
    };
  }, [onClose]);

  return (
    <motion.aside
      ref={menuRef}
      key="simplified-menu"
      role="dialog"
      aria-modal="true"
      aria-label="Site navigation"
      tabIndex={-1}
      initial={reduceMotion ? false : { opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: reduceMotion ? 0 : -8 }}
      transition={{ duration: reduceMotion ? 0 : 0.45, ease }}
      className="fixed inset-0 z-[120] overflow-y-auto bg-[#f2f2f2] text-[#191919]"
    >
      <div className="mx-auto flex min-h-full max-w-[1600px] flex-col px-6 sm:px-10 md:px-12 lg:px-16">
        <header className="flex min-h-20 items-center justify-between border-b border-black/15 md:min-h-24">
          <Link
            to="/"
            onClick={(event) => {
              event.preventDefault();
              onRouteSelect("/");
            }}
            onMouseEnter={onNavIntent}
            onFocus={onNavIntent}
            aria-label="codelumen home"
            className="hidden items-center gap-4 focus:outline-none focus-visible:ring-1 focus-visible:ring-black/60 md:inline-flex"
          >
            <img
              src={logoWhite}
              alt="codelumen"
              width={176}
              height={28}
              className="h-5 w-auto invert sm:h-6"
            />
          </Link>

          <div className="pointer-events-none absolute left-1/2 -translate-x-1/2">
            <LogoBars
              key={`nav-logo-desktop-${navLogoTriggerId}`}
              variant={navLogoSequenceActive ? "progress" : "idle"}
              progress={navLogoProgress}
              tone="dark"
              glowTone="neutral"
              size="md"
              ariaLabel="codelumen — four-layer system"
              className="hidden md:inline-flex"
            />
            <LogoBars
              key={`nav-logo-mobile-${navLogoTriggerId}`}
              variant="sequence"
              triggerKey={navLogoTriggerId}
              tone="dark"
              glowTone="neutral"
              size="sm"
              ariaLabel="codelumen — four-layer system"
              className="inline-flex md:hidden"
            />
          </div>

          <div className="ml-auto flex items-center gap-4">
            <span className="hidden text-[9px] font-medium uppercase tracking-[0.28em] text-black/45 sm:block">
              Navigation
            </span>
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              aria-label="Close menu"
              className="inline-flex min-h-11 min-w-11 items-center justify-center bg-transparent p-0 text-black/65 transition-colors hover:text-black focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-black/60"
            >
              <X className="h-5 w-5" strokeWidth={1.25} />
            </button>
          </div>
        </header>

        <main className="grid flex-1 grid-cols-1 gap-12 py-10 md:grid-cols-12 md:gap-x-12 md:py-14 lg:gap-x-16 lg:py-16">
          <section
            className="md:col-span-9 md:col-start-1 md:row-start-1"
            aria-labelledby="primary-navigation"
          >
            <p
              id="primary-navigation"
              className="mb-4 text-[9px] font-medium uppercase tracking-[0.28em] text-black/45 md:mb-7"
            >
              Main
            </p>

            <nav aria-label="Primary navigation">
              {primaryRoutes.map((route, index) => {
                const active = location.pathname === route.href;
                return (
                  <motion.div
                    key={route.href}
                    initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{
                      duration: reduceMotion ? 0 : 0.5,
                      delay: reduceMotion ? 0 : 0.05 + index * 0.05,
                      ease,
                    }}
                    className="border-t border-black/15 last:border-b"
                  >
                    <Link
                      to={route.href}
                      onClick={(event) => {
                        event.preventDefault();
                        onRouteSelect(route.href);
                      }}
                      onMouseEnter={onNavIntent}
                      onFocus={onNavIntent}
                      aria-current={active ? "page" : undefined}
                      className="group grid min-h-[5.75rem] grid-cols-[2rem_minmax(0,1fr)] items-center gap-x-3 py-4 focus:outline-none focus-visible:bg-black/[0.035] sm:grid-cols-[3rem_minmax(0,1fr)_minmax(13rem,0.68fr)_2rem] sm:gap-x-5 md:min-h-[6.25rem] md:py-5"
                    >
                      <span className="self-start pt-2 text-[9px] font-medium tracking-[0.22em] text-black/40 sm:self-center sm:pt-0">
                        {route.index}
                      </span>
                      <span
                        className={`text-[clamp(1.75rem,3.2vw,3.25rem)] font-normal leading-[0.98] tracking-[-0.035em] transition-transform duration-500 ease-out group-hover:translate-x-1 ${
                          active ? "text-black" : "text-black/88"
                        }`}
                      >
                        {route.label}
                      </span>
                      <span className="col-start-2 mt-2.5 max-w-[24rem] text-[0.8125rem] font-normal leading-5 text-black/55 sm:col-start-auto sm:mt-0 sm:text-sm sm:leading-5">
                        {route.note}
                      </span>
                      <ArrowUpRight
                        className="hidden h-5 w-5 text-black/35 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-black sm:block"
                        strokeWidth={1.25}
                        aria-hidden
                      />
                    </Link>
                  </motion.div>
                );
              })}
            </nav>
          </section>

          <section
            aria-labelledby="menu-actions"
            className="border-t border-black/15 pt-7 md:col-span-9 md:col-start-1 md:row-start-2 md:mt-8"
          >
            <p
              id="menu-actions"
              className="mb-5 text-[9px] font-medium uppercase tracking-[0.28em] text-black/45"
            >
              Start
            </p>
            <div className="md:grid md:grid-cols-2 md:gap-5">
              <button
                type="button"
                onClick={onProjectReview}
                onMouseEnter={onNavIntent}
                onFocus={onNavIntent}
                className="group flex min-h-12 w-full items-center justify-between bg-black px-4 text-left text-[0.6875rem] font-medium uppercase tracking-[0.12em] text-white transition-colors hover:bg-black/85 focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-black/60"
              >
                <span>Request a Project Review</span>
                <span aria-hidden className="ml-3 text-white/55 transition-transform duration-300 group-hover:translate-x-0.5">
                  →
                </span>
              </button>
              <Link
                to="/labs#booking"
                onClick={(event) => {
                  event.preventDefault();
                  onRouteSelect("/labs#booking");
                }}
                onMouseEnter={onNavIntent}
                onFocus={onNavIntent}
                className="group mt-2 flex min-h-11 items-center justify-between border-b border-black/15 text-[0.75rem] font-medium uppercase tracking-[0.1em] text-black/65 transition-colors hover:text-black focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-black/60 md:mt-0 md:min-h-12 md:border md:px-4"
              >
                <span>Schedule a Labs Session</span>
                <span aria-hidden className="text-black/30 transition-transform duration-300 group-hover:translate-x-0.5">
                  →
                </span>
              </Link>
            </div>
          </section>

          <aside className="-mt-2 md:col-span-3 md:col-start-10 md:row-span-2 md:row-start-1 md:mt-0 md:border-l md:border-black/15 md:pl-8 md:pt-9 lg:pl-12">
            <div>
              <p className="mb-5 text-[9px] font-medium uppercase tracking-[0.28em] text-black/45">
                More
              </p>
              <nav aria-label="Secondary navigation" className="space-y-1">
              {secondaryRoutes.map((route) => (
                <Link
                  key={route.href}
                  to={route.href}
                  onClick={(event) => {
                    event.preventDefault();
                    onRouteSelect(route.href);
                  }}
                  onMouseEnter={onNavIntent}
                  onFocus={onNavIntent}
                  aria-current={location.pathname === route.href ? "page" : undefined}
                  className="group flex min-h-11 items-center justify-between border-b border-black/10 text-base font-normal text-black/70 transition-colors hover:text-black focus:outline-none focus-visible:ring-1 focus-visible:ring-black/60"
                >
                  <span>{route.label}</span>
                  <span className="text-black/30 transition-transform duration-300 group-hover:translate-x-0.5">
                    →
                  </span>
                </Link>
              ))}
              </nav>
            </div>

            <div className="mt-10 md:mt-12">
              <p className="mb-3 text-[9px] font-medium uppercase tracking-[0.28em] text-black/45">
                Direct
              </p>
              <a
                href="mailto:info@clumen.com"
                className="inline-block border-b border-black/25 pb-1 text-sm font-normal text-black/70 transition-colors hover:text-black focus:outline-none focus-visible:ring-1 focus-visible:ring-black/60"
              >
                info@clumen.com
              </a>
            </div>

            <div className="mt-10 border-t border-black/15 pt-7 md:mt-12">
              <p className="mb-4 text-[9px] font-medium uppercase tracking-[0.28em] text-black/45">
                Follow
              </p>
              <div className="flex flex-col gap-3">
                <a
                  href="https://www.linkedin.com/company/clumen/"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="LinkedIn · clumen"
                  className="group inline-flex min-h-11 items-center gap-3 text-sm text-black/60 transition-colors hover:text-black focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-black/60"
                >
                  <svg aria-hidden width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                  <span>LinkedIn · clumen</span>
                </a>
                <a
                  href="https://www.instagram.com/codelumen"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Instagram · @codelumen"
                  className="group inline-flex min-h-11 items-center gap-3 text-sm text-black/60 transition-colors hover:text-black focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-black/60"
                >
                  <svg aria-hidden width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                  <span>Instagram · @codelumen</span>
                </a>
              </div>
            </div>
          </aside>
        </main>

        <footer className="flex min-h-16 flex-wrap items-center justify-between gap-3 border-t border-black/15 py-4 text-[9px] font-medium uppercase tracking-[0.2em] text-black/40">
          <Link
            to="/"
            onClick={(event) => {
              event.preventDefault();
              onRouteSelect("/");
            }}
            onMouseEnter={onNavIntent}
            onFocus={onNavIntent}
            aria-label="codelumen home"
            className="inline-flex min-h-11 items-center focus:outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-black/60 md:hidden"
          >
            <img
              src={logoWhite}
              alt="codelumen"
              width={176}
              height={28}
              className="h-5 w-auto invert"
            />
          </Link>
          <span className="hidden md:inline">Lighting, held end to end.</span>
          <span>© 2026 codelumen</span>
        </footer>
      </div>
    </motion.aside>
  );
};

export default SimplifiedMenuOverlay;
